import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import * as AntD from 'antd'
import {
  Controller,
  useFormContext,
  get as getValueFromPath
} from 'react-hook-form'

import { InfractionFlag } from 'admin/lib/Records'
import { FormItemContainer } from './internals'
import { AutoCompleteInput } from './AutoCompleteInput'

/**
 * @param {{ label: any, rules: import("react-hook-form").ValidationRules }} param0
 */
export function FormItem({
  label,
  name = '',
  altNames = [],
  children = <AntD.Input />,
  defaultValue: defaultValueProp = '',
  rules = null,
  inputRef = null,
  hidden = false,
  disabled = false,
  simpleDataValidationRules = null,
  uppercaseInput = false,
  contextLink = null,
  instrumented = false,
  ...props
}) {
  const internalRef = useRef()
  const ref = inputRef ?? internalRef
  const { control, errors } = useFormContext()

  const child = React.Children.only(children)
  const { onFocus, onBlur } = props

  const defaultValues = control.defaultValuesRef.current
  const defaultValue = useMemo(
    () => getValueFromPath(defaultValues ?? {}, name, undefined),
    [defaultValues, name]
  )

  const [simpleDataValidationWarnings, setSimpleDataValidationWarnings] =
    useState([])

  const error = getValueFromPath(errors ?? {}, name, null)

  const infractionPaths = useMemo(() => {
    if (name.startsWith('uniquePrefix')) {
      // If line item add altNames
      return [
        name,
        ...altNames,
        name.replace('uniquePrefix', 'data.items'), // For Waste line item
        name.replace('uniquePrefix', 'data.products') // For Delivery line item
      ]
    }
    return [name, ...altNames]
  }, [altNames, name])

  const runInputValidation = useCallback(
    value => {
      if (value && rules && rules.validate) {
        rules.validate.checkValue(value)
      }
    },
    [rules]
  )

  const runSimpleDataValidation = useCallback(
    value => {
      if (!simpleDataValidationRules) {
        return
      }
      const warnings = simpleDataValidationRules(value)
      setSimpleDataValidationWarnings(warnings)
    },
    [simpleDataValidationRules]
  )

  // This effect is to trigger the validation on the first load.
  //
  // However, in the future we probably also require this to trigger any time the form is reset (see Paste functionality).
  // But - It looks like we use reset to set some of the date fields (see Waste.js and Delivery.js usages of reset())
  //
  // The Lint exclusion is because adding those other dependencies causes the Validation to break on the dates (Arrival/Exit, Delivery/Exit)
  // and you can actively see them triggering the Effect at strange times.
  useEffect(() => {
    // Line-Item do not appear to pass in the default value at the correct time
    // So if no defaultValue is set we grab the value using control.getValues
    const value = defaultValue ? defaultValue : control.getValues(name)

    runInputValidation(value)
    runSimpleDataValidation(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue])

  const cleanInputValue = useCallback(
    value => {
      if (typeof value !== 'string') {
        return value
      }

      value = value.trim().replace(/\s+/g, ' ')

      if (uppercaseInput) {
        value = value.toUpperCase()
      }

      return value
    },
    [uppercaseInput]
  )

  const handleInputFocus = e => {
    if (instrumented) {
      performance.mark('field-entered', { detail: { fieldName: `${name}` } })
    }

    if (onFocus) {
      onFocus(e)
    }
  }

  const handleInputBlur = useCallback(
    async e => {
      // NOTE:
      // Some inputs (Select) do not use event.target.value so we should always use control.getValues/setValues.
      // We must ensure we setValue with the cleaned value before calling the custom onBlur as those handlers
      // should also always use getValues/setValues not target.value.
      const originalValue = control.getValues(name)
      control.setValue(name, cleanInputValue(originalValue), {
        shouldDirty: true // Re-evaluate isDirty
      })

      if (instrumented) {
        performance.mark('field-exited', { detail: { fieldName: `${name}` } })
        performance.measure('field-duration', {
          start: 'field-entered',
          end: 'field-exited',
          detail: { fieldName: `${name}` }
        })
      }

      if (onBlur) {
        onBlur(e)
      }

      // Be sure to get the latest version to run the validation on; onBlur may have altered it.
      const value = control.getValues(name)
      runInputValidation(value)
      runSimpleDataValidation(value)
    },
    [
      cleanInputValue,
      control,
      instrumented,
      name,
      onBlur,
      runInputValidation,
      runSimpleDataValidation
    ]
  )

  return (
    <Controller
      name={name}
      defaultValue={defaultValueProp ?? defaultValue}
      rules={rules}
      onFocus={() => ref.current?.focus?.()}
      render={inputProps => (
        <FormItemLayout
          {...props}
          infractionPaths={infractionPaths}
          label={label}
          error={error}
          hidden={hidden}
          disabled={disabled}
          className={
            error
              ? 'contains-validation-error'
              : simpleDataValidationWarnings.length > 0
                ? 'contains-validation-warning'
                : ''
          }
          contextLink={contextLink}
        >
          {React.cloneElement(child, {
            ...inputProps,
            id: props.id,
            onFocus: handleInputFocus,
            onBlur: handleInputBlur,
            onChange: e => {
              inputProps.onChange(e)
              if (simpleDataValidationWarnings.length > 0) {
                runSimpleDataValidation(control.getValues(name))
              }
            },
            disabled:
              typeof child.props.disabled !== 'boolean'
                ? disabled
                : child.props.disabled,
            hidden,
            ref
          })}
          {!hidden && error && (
            <p className="validation-error">
              {label} {error.message || `Error '${error.type}'`}
            </p>
          )}
          {!hidden &&
            simpleDataValidationWarnings.map((w, i) => (
              <p key={i} className="validation-warning">
                {label} {w}
              </p>
            ))}
        </FormItemLayout>
      )}
    />
  )
}

FormItem.propTypes = {
  label: PropTypes.any,
  name: PropTypes.string,
  id: PropTypes.string,
  altNames: PropTypes.arrayOf(PropTypes.string),
  style: PropTypes.object,
  onBlur: PropTypes.any,
  onFocus: PropTypes.any,
  children: PropTypes.node,
  defaultValue: PropTypes.any,
  hidden: PropTypes.bool,
  disabled: PropTypes.bool,
  rules: PropTypes.object,
  inputRef: PropTypes.object,
  simpleDataValidationRules: PropTypes.func,
  uppercaseInput: PropTypes.bool,
  contextLink: PropTypes.node,
  instrumented: PropTypes.bool
}

/**
 * @param {{ label: any, rules: import("react-hook-form").ValidationRules }} param0
 */
export function AutoCompleteFormItem({
  label,
  name = '',
  altNames = [],
  children = <AntD.Input />,
  defaultValue: defaultValueProp = '',
  rules = null,
  inputRef = null,
  hidden = false,
  disabled = false,
  simpleDataValidationRules = null,
  uppercaseInput = false,
  instrumented = false,
  ...props
}) {
  return (
    <FormItem
      label={label}
      name={name}
      altNames={altNames}
      defaultValue={defaultValueProp}
      rules={rules}
      inputRef={inputRef}
      hidden={hidden}
      disabled={disabled}
      simpleDataValidationRules={simpleDataValidationRules}
      uppercaseInput={uppercaseInput}
      instrumented={instrumented}
      {...props}
    >
      <AutoCompleteInput {...props}>{children}</AutoCompleteInput>
    </FormItem>
  )
}

AutoCompleteFormItem.propTypes = FormItem.propTypes

const EMPTY_ARR = []
export const FormItemLayout = ({
  infractionPaths = EMPTY_ARR,
  label,
  error = null,
  children,
  hidden = false,
  disabled = false,
  contextLink = null,
  ...props
}) => {
  if (hidden) {
    return children
  }

  const containerProps = { ...props }
  delete containerProps.id

  return (
    <FormItemContainer {...containerProps}>
      <div>
        <AntD.Space direction="horizontal">
          <InfractionFlag names={infractionPaths} />

          {label && (
            <FormItemLabel id={props.id}>
              <AntD.Typography.Text
                type={error ? 'danger' : ''}
                style={{
                  flex: '1 1 0',
                  overflow: 'hidden',
                  opacity: disabled ? '0.7' : '1.0'
                }}
              >
                {label}
              </AntD.Typography.Text>
            </FormItemLabel>
          )}
        </AntD.Space>

        {contextLink && <span style={{ float: 'right' }}>{contextLink}</span>}
      </div>

      {children}
    </FormItemContainer>
  )
}

FormItemLayout.propTypes = {
  infractionPaths: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  error: PropTypes.object,
  children: PropTypes.node.isRequired,
  hidden: PropTypes.bool,
  disabled: PropTypes.bool
}

const FormItemLabel = ({ id, children }) => {
  if (!id) {
    return children
  }
  return (
    <label htmlFor={id} style={{ color: 'inherit', fontSize: 'inherit' }}>
      {children}
    </label>
  )
}
